/**
   * Resolves the selected option values to a variant and updates the form,
   * the price, the buy button and the URL. Also tells the gallery which image
   * to show when the variant has its own featured media.
   */
  class ZsVariants extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      if (!json) return;

      try {
        this.variants = JSON.parse(json.textContent);
      } catch (error) {
        // A malformed blob should not take the buy button down with it.
        console.error('[zs-variants] could not parse variant data', error);
        return;
      }

      this.form = document.getElementById(this.dataset.formId);
      this.root = this.closest('[data-product-root]') || document;

      this.addEventListener('change', () => this.onChange());
      this.onChange({ initial: true });
    }

    get selectedValues() {
      return Array.from(this.querySelectorAll('input:checked')).map((input) => input.value);
    }

    onChange(options) {
      const selected = this.selectedValues;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((value, index) => value === selected[index])
      );

      this.querySelectorAll('fieldset').forEach(group => {
        const selection = group.querySelector('[data-selected-option]');
        if (selection) selection.textContent = group.querySelector('input:checked')?.value || '';
      });
      this.updateForm(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateAvailability(selected);

      if (variant && !(options && options.initial)) {
        this.updateUrl(variant);
        this.updateGallery(variant);
      }
    }

    updateForm(variant) {
      if (!this.form) return;
      const input = this.form.querySelector('input[name="id"]');
      if (input) { input.value = variant ? variant.id : ''; input.disabled = !variant || !variant.available; }
    }

    updatePrice(variant) {
      const target = this.root.querySelector('[data-price]');
      if (!target) return;

      const compare = this.root.querySelector('[data-compare-price]');
      if (compare) {
        compare.hidden = !variant || !(variant.compare_at_price > variant.price);
        compare.textContent = variant ? this.formatMoney(variant.compare_at_price || 0) : '';
      }
      if (!variant) {
        target.textContent = '';
        return;
      }

      target.textContent = this.formatMoney(variant.price);
    }

    /**
     * Shopify serialises prices in cents. The shop's money format travels on
     * the price element as a template with one placeholder, so the currency
     * symbol and its placement stay under the merchant's control.
     *
     * Grouping follows the placeholder name: the comma-separator variants use
     * `1.234,56`, the rest use `1,234.56`.
     */
    formatMoney(cents) {
      const target = this.root.querySelector('[data-price]');
      const format = target && target.dataset.moneyFormat;
      const placeholder = format && format.match(/\{\{\s*(\w+)\s*\}\}/);

      if (!placeholder) return (cents / 100).toFixed(2);

      const useCommaDecimal = placeholder[1].indexOf('comma_separator') !== -1;
      const withoutDecimals = placeholder[1].indexOf('no_decimals') !== -1;

      const fixed = (cents / 100).toFixed(withoutDecimals ? 0 : 2);
      const parts = fixed.split('.');
      const groupSeparator = useCommaDecimal ? '.' : ',';
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);

      const amount = parts.join(useCommaDecimal ? ',' : '.');
      return format.replace(placeholder[0], amount);
    }

    updateButton(variant) {
      const button = this.root.querySelector('[data-add-to-cart]');
      if (!button) return;

      const available = Boolean(variant && variant.available);
      button.disabled = !available;

      const label = button.querySelector('[data-add-to-cart-text]');
      if (!label) return;

      if (!variant) {
        label.textContent = button.dataset.unavailableText;
      } else if (!variant.available) {
        label.textContent = button.dataset.soldOutText;
      } else {
        label.textContent = button.dataset.defaultText;
      }
    }

    updateAvailability(selected) {
      // Mark values that cannot combine with the current selection of every
      // preceding option — the usual "greyed out but still clickable" model.
      this.querySelectorAll('input').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const candidate = selected.slice();
        candidate[position] = input.value;

        const exists = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options
              .slice(0, position + 1)
              .every((value, index) => value === candidate[index])
        );

        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label) label.dataset.unavailable = exists ? 'false' : 'true';
      });
    }

    updateUrl(variant) {
      if (!this.dataset.url || this.closest('dialog')) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    updateGallery(variant) {
      if (!variant.featured_media) return;
      const gallery = this.root.querySelector('[data-gallery]');
      if (!gallery) return;

      gallery.dispatchEvent(
        new CustomEvent('zs:gallery:select', {
          detail: { index: variant.featured_media.position - 1 },
        })
      );
    }
  }

  if (!customElements.get('zs-variants')) {
    customElements.define('zs-variants', ZsVariants);
  }
